// 見通し章「これからの見通し」のデータ整形・総評ロジック（純関数のみ）。
// バックエンド reinfolib.py の _prospects_metrics / _residential_inducement が組み立てる
// metrics をここで型付きに読み直す。語りの文体は本サービスの文体（断定しない・煽らない・
// 正直に・最後は読者の確認行動につなぐ）に従う。

import type { ReinfoLayerSummary, ReinfoPortfolio } from '@/lib/api.types';

export type YearValue = { year: number; value: number };

export type PopulationProspect = {
  series: YearValue[];
  baseYear: number;
  base: number;
  targetYear: number;
  target: number;
  changePct: number;
  agingBasePct?: number;
  agingTargetPct?: number;
};

export type StationTrend = {
  series: YearValue[];
  baseYear: number;
  base: number;
  latestYear: number;
  latest: number;
  changePct: number;
  station?: string;
  line?: string;
};

export type ProspectsMetrics = {
  population?: PopulationProspect;
  stationTrend?: StationTrend;
  checkItems: string[];
};

function asNumber(v: unknown): number | undefined {
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
}

function asRecord(v: unknown): Record<string, unknown> | null {
  return v != null && typeof v === 'object' && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : null;
}

// {"2025": 12000, "2050": 9500} 形式の series を年順の配列へ。
function parseSeries(v: unknown): YearValue[] {
  const rec = asRecord(v);
  if (!rec) return [];
  const out: YearValue[] = [];
  for (const [key, value] of Object.entries(rec)) {
    const year = Number(key);
    const num = asNumber(value);
    if (Number.isInteger(year) && num !== undefined) out.push({ year, value: num });
  }
  return out.sort((a, b) => a.year - b.year);
}

/** life_environment ポートフォリオの metrics["prospects"] を型付きで読む。 */
export function parseProspectsMetrics(
  metrics: Record<string, unknown> | undefined | null,
): ProspectsMetrics | null {
  const prospects = asRecord(metrics?.['prospects']);
  if (!prospects) return null;

  let population: PopulationProspect | undefined;
  const pop = asRecord(prospects['population']);
  if (pop) {
    const series = parseSeries(pop['series']);
    const baseYear = asNumber(pop['base_year']);
    const base = asNumber(pop['base']);
    const targetYear = asNumber(pop['target_year']);
    const target = asNumber(pop['target']);
    const changePct = asNumber(pop['change_pct']);
    if (
      series.length >= 2 &&
      baseYear !== undefined &&
      base !== undefined &&
      targetYear !== undefined &&
      target !== undefined &&
      changePct !== undefined
    ) {
      population = {
        series,
        baseYear,
        base,
        targetYear,
        target,
        changePct,
        agingBasePct: asNumber(pop['aging_base_pct']),
        agingTargetPct: asNumber(pop['aging_target_pct']),
      };
    }
  }

  let stationTrend: StationTrend | undefined;
  const st = asRecord(prospects['station_trend']);
  if (st) {
    const series = parseSeries(st['series']);
    const baseYear = asNumber(st['base_year']);
    const base = asNumber(st['base']);
    const latestYear = asNumber(st['latest_year']);
    const latest = asNumber(st['latest']);
    const changePct = asNumber(st['change_pct']);
    if (
      series.length >= 2 &&
      baseYear !== undefined &&
      base !== undefined &&
      latestYear !== undefined &&
      latest !== undefined &&
      changePct !== undefined
    ) {
      stationTrend = {
        series,
        baseYear,
        base,
        latestYear,
        latest,
        changePct,
        station: typeof st['station'] === 'string' ? st['station'] : undefined,
        line: typeof st['line'] === 'string' ? st['line'] : undefined,
      };
    }
  }

  const checkItems = Array.isArray(prospects['check_items'])
    ? (prospects['check_items'] as unknown[]).filter((i): i is string => typeof i === 'string')
    : [];

  if (!population && !stationTrend) return null;
  return { population, stationTrend, checkItems };
}

// -------------------- 居住誘導区域（立地適正化計画） --------------------

// backend _residential_inducement と同じ4状態。
export type Inducement = 'inside' | 'zone_other' | 'outside' | 'unknown';

export function parseInducement(
  planning: ReinfoPortfolio | undefined | null,
): Inducement | null {
  const v = planning?.metrics?.['residential_inducement'];
  return v === 'inside' || v === 'zone_other' || v === 'outside' || v === 'unknown' ? v : null;
}

// -------------------- DID（人口集中地区）の詳細 --------------------

export type DidDetails = {
  population?: number;
  areaKm2?: number;
  prevPopulation?: number;
  censusYear?: number;
  /** 前回調査からの人口増減率(%)。 */
  changePct?: number;
};

/** XKT031 の representative_properties（A16_005=人口, A16_006=面積km², A16_007=前回人口, A16_011=調査年）。 */
export function parseDidDetails(layer: ReinfoLayerSummary | undefined): DidDetails | null {
  const props = layer?.representative_properties;
  if (!props) return null;
  const population = asNumber(props['A16_005']);
  const areaKm2 = asNumber(props['A16_006']);
  const prevPopulation = asNumber(props['A16_007']);
  const censusYear = asNumber(props['A16_011']);
  if (population === undefined && areaKm2 === undefined) return null;
  const changePct =
    population !== undefined && prevPopulation !== undefined && prevPopulation > 0
      ? Math.round(((population - prevPopulation) / prevPopulation) * 1000) / 10
      : undefined;
  return { population, areaKm2, prevPopulation, censusYear, changePct };
}

// -------------------- 総評（4段階の語り） --------------------

// スコアや偏差値は採用しない（推計の不確実性をひと桁の数字が覆い隠し「予言」に見えるため）。
// 増減率だけで語りの段階を決め、DID・居住誘導区域は段階を上下させず本文と根拠チップで併記する。
// 境界(−5/−25)は chapters.ts の POP_TREND_BANDS・backend _prospects_metrics と同一の正準バンド。
export type ProspectTier = 'flowing' | 'gentle' | 'declining' | 'unknown';

export function classifyProspectTier(changePct: number | null | undefined): ProspectTier {
  if (changePct == null || !Number.isFinite(changePct)) return 'unknown';
  if (changePct > -5) return 'flowing';
  if (changePct > -25) return 'gentle';
  return 'declining';
}

export type ProspectVerdict = { heading: string; body: string };

/** 総評カード「ひとことでいうと」の語り。tier テンプレ + DID内外で一文差し替え + 区域外なら注意一文。 */
export function prospectVerdict(opts: {
  tier: ProspectTier;
  targetYear?: number;
  changePct?: number;
  didInside: boolean | null;
  inducement: Inducement | null;
}): ProspectVerdict {
  const { tier, targetYear, changePct, didInside, inducement } = opts;
  const year = targetYear ?? 2050;

  if (tier === 'unknown') {
    return {
      heading: '数字だけでは見通せない土地',
      body:
        'この付近の人口推計は得られませんでした（メッシュが粗い・秘匿などの理由があります）。' +
        '数字が無いことは、見通しが悪いことを意味しません。市区町村の人口ビジョンをあわせてご覧ください。',
    };
  }

  const didSentence =
    didInside === true
      ? '市街地のまとまり（人口集中地区）にも含まれており、お店や交通といったまちの営みは保たれやすいとみられます。'
      : didInside === false
        ? '市街地のまとまり（人口集中地区）の外側にあり、静けさと暮らしの便のつりあいを現地で確かめたいところです。'
        : '';

  // outside は「周辺タイルで確認できた範囲での非該当」にすぎない（隣接自治体の計画だけが
  // 写り込んだ場合や、タイル一部欠落でも outside になり得る）ため、市の判断を断定する言い方はしない。
  const inducementSentence =
    inducement === 'outside'
      ? 'なお、周辺で確認できた居住誘導区域（市町村が住まいを誘導すると定めた範囲）には含まれていませんでした。お住まいになる市町村が立地適正化計画を定めているかは、区域図とあわせて確かめておくと安心です。'
      : '';

  let heading: string;
  let lead: string;
  if (tier === 'flowing') {
    heading = '人の流れがつづくとみられるまちなみ';
    lead = `この土地のまわりの人口は、${year}年ごろまで大きくは減らない推計です。`;
  } else if (tier === 'gentle') {
    heading = 'ゆるやかに変わっていくまちなみ';
    lead = `この土地のまわりの人口は、${year}年にかけてゆるやかに減っていく推計です。`;
  } else {
    const pct = changePct != null ? Math.abs(Math.round(changePct)) : null;
    heading = 'ゆっくりと人が減っていくまちなみ';
    lead =
      `この土地のまわりの人口は、${year}年にかけて${pct != null ? `${pct}%ほど` : 'いまより'}少なくなる推計です。` +
      'すぐに暮らしが変わるわけではありませんが、お店や学校、バスの便が長い時間をかけて姿を変えていく可能性は、心に留めておきたいところです。';
  }

  const body = [lead, tier === 'declining' ? '' : didSentence, inducementSentence]
    .filter(Boolean)
    .join('');
  return { heading, body };
}

/** 「+7.7%」「−20.8%」の符号付き表示（端数1桁）。 */
export function formatChangePct(changePct: number): string {
  const sign = changePct > 0 ? '+' : changePct < 0 ? '−' : '±';
  return `${sign}${Math.abs(changePct).toFixed(1)}%`;
}
